import { DEFAULT_OG_IMAGE } from "../../../shared/seo/page-seo";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams, useRouterState } from "@tanstack/react-router";
import { useQueries, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { useTranslation } from "react-i18next";
import { CONTACT_EMAIL, WHATSAPP_NUMBER_E164, OG_LOCALE, langSegmentRegex, SUPPORTED_LANGS, isTransferBooking, type Lang } from "@majourneys/shared";
import { ActivityCard2 } from "../../../shared/ui/activity-card-2";
import { panelStyle } from "../../../shared/ui/card-frame";
import { pewterFrame, pewterInner, solid } from "../../../shared/ui/pewter-edge";
import {
  CATEGORY_PILL_CLASS,
  CategoryPillBody,
  categoryPillFrameStyle,
} from "../../../shared/ui/category-pill";
import { queueCategoryFx } from "../../../shared/ui/category-tap-fx";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { HeroSection } from "../components/hero-section";
import { useCategories } from "../hooks/use-categories";
import { useCities } from "../hooks/use-cities";
import { useTrendingActivities } from "../hooks/use-trending-activities";
import { useNewActivities } from "../hooks/use-new-activities";
import { useOffers } from "../hooks/use-offers";
import { activitiesApi } from "../../activities/api/activities.api";
import { activityKeys } from "../../activities/constants/query-keys";
import { activityHrefString } from "../../activities/lib/activity-href";
import { rankCategories } from "../../activities/lib/category-rank";
import { ReelsSection } from "../components/reels-section";
import { getMoroccoName } from "../../../shared/lib/locale";
import { recentViewedDisplay, removeRecentViewed, useRecentViewed } from "../../../shared/hooks/use-recent-viewed";
import { GlassConciergeLauncher, useHeroSentinel } from "../../ai-concierge";

import { Icon } from "@majourneys/icons";
import { ResumeBookingCard } from "../components/resume-booking-card";
import { CityCard, type City } from "../components/city-card";
import { AppSection } from "../components/app-section";
import { WomenCategoryChip, WomenHomeBanner } from "../../women/components/women-home-entry";
// citySlug maps a region to its `cities` row for the photo + link ("fes" → "fez").
// fallbackImage covers regions without a city image. Merzouga left the row
// (owner, 2026-10-15): it has no city page to link to.
const REGIONS = [
  {
    key: "marrakech",
    citySlug: "marrakech",
    fallbackImage: "https://images.unsplash.com/photo-1597212618440-806262de4f6b?w=1200",
  },
  {
    key: "casablanca",
    citySlug: "casablanca",
    fallbackImage: "https://images.unsplash.com/photo-1538230575309-59dfc388ae36?w=1200",
  },
  {
    key: "fes",
    citySlug: "fez",
    fallbackImage: "https://images.unsplash.com/photo-1548013146-72479768bada?w=1200",
  },
  {
    key: "chefchaouen",
    citySlug: "chefchaouen",
    fallbackImage: "https://images.unsplash.com/photo-1553244651-9e0c2cb3e1e3?w=1200",
  },
  {
    key: "essaouira",
    citySlug: "essaouira",
    fallbackImage: "https://images.unsplash.com/photo-1572206912757-5a78ff4d79be?w=1200",
  },
  {
    key: "agadir",
    citySlug: "agadir",
    fallbackImage: "https://images.unsplash.com/photo-1565552645632-d725f8bfc19a?w=1200",
  },
  {
    key: "ouarzazate",
    citySlug: "ouarzazate",
    fallbackImage: "https://images.unsplash.com/photo-1489493887464-892be6d1daae?w=1200",
  },
] as const;

export function HomePage() {
  const { t } = useTranslation();
  const heroEndRef = useRef<HTMLDivElement>(null);
  useHeroSentinel(heroEndRef);

  return (
    <div className="relative">
      <HomeSeo />
      <BackgroundOrbs />

      <div className="hidden lg:block">
        <Suspense fallback={<HeroSkeleton />}>
          <HeroSection />
        </Suspense>
      </div>
      {/* Tells the AI pill when the hero's own search has scrolled away. */}
      <div ref={heroEndRef} aria-hidden className="h-px" />

      {/* A booking left part way, first thing on the page (round 18, item 146). */}
      <ResumeBookingCard />

      <div>
        <Suspense fallback={null}>
          <CategoryStrip />
        </Suspense>
      </div>

      {/* Women-only trips (owner, 2026-10-27): the pink banner sits between
          the categories and the cities; it shows only with a trip coming up. */}
      <WomenHomeEntryBanner />

      <Suspense fallback={null}>
        <CitiesCarousel />
      </Suspense>


      <RecentViewedRail />

      <Suspense fallback={null}>
        <TrendingRail />
      </Suspense>

      <Suspense fallback={null}>
        <ReelsSection />
      </Suspense>

      <Suspense fallback={<RailSkeleton />}>
        <CityActivityRail
          slug="marrakech"
          eyebrow={t("home.city_marrakech_eyebrow", { defaultValue: "City" })}
          title={t("home.city_marrakech_title", { defaultValue: "Marrakech" })}
          subtitle={t("home.city_marrakech_subtitle", { defaultValue: "Souks, palaces, and quad rides in the Palmeraie." })}
        />
      </Suspense>

      <Suspense fallback={<RailSkeleton />}>
        <CityActivityRail
          slug="casablanca"
          eyebrow={t("home.city_casablanca_eyebrow", { defaultValue: "City" })}
          title={t("home.city_casablanca_title", { defaultValue: "Casablanca" })}
          subtitle={t("home.city_casablanca_subtitle", { defaultValue: "Atlantic coast, nightlife, and jet ski charters." })}
        />
      </Suspense>

      <Suspense fallback={<RailSkeleton />}>
        <CityActivityRail
          slug="dakhla"
          eyebrow={t("home.city_dakhla_eyebrow", { defaultValue: "City" })}
          title={t("home.city_dakhla_title", { defaultValue: "Dakhla" })}
          subtitle={t("home.city_dakhla_subtitle", { defaultValue: "Lagoon kitesurfing, dunes, and desert beach camps." })}
        />
      </Suspense>

      <Suspense fallback={<RailSkeleton />}>
        <CityActivityRail
          slug="agafay"
          eyebrow={t("home.city_agafay_eyebrow", { defaultValue: "City" })}
          title={t("home.city_agafay_title", { defaultValue: "Agafay" })}
          subtitle={t("home.city_agafay_subtitle", { defaultValue: "Stone desert camps, camel rides, and sunset dinners." })}
        />
      </Suspense>

      <Suspense fallback={null}>
        <OffersRail />
      </Suspense>

      <Suspense fallback={null}>
        <NewRail />
      </Suspense>

      <div className="hidden md:block">
        <Suspense fallback={null}>
          <RegionsSection />
        </Suspense>
      </div>

      <AppSection />

      <div className="hidden md:block">
        <FaqSection />
      </div>

      <div className="hidden md:block">
        <Suspense fallback={null}>
          <SeoFooterSection />
        </Suspense>
      </div>

      <GlassConciergeLauncher />
    </div>
  );
}

function HomeSeo() {
  const { t } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const countryName = getMoroccoName(lang);
  const title = t("home.seo_title", { country: countryName });
  const description = t("home.seo_description", { country: countryName });
  const keywords = t("home.seo_keywords");
  const faqs = t("home.faqs", { returnObjects: true }) as ReadonlyArray<{ q: string; a: string }>;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const siteOrigin = (import.meta.env.VITE_SITE_URL ?? "https://majourneys.com").replace(/\/+$/, "");
  const url = `${siteOrigin}${pathname}`;
  const ogLocale = OG_LOCALE[lang as Lang] ?? OG_LOCALE.en;
  // Build /<l> alternates from the current pathname by swapping the locale
  // segment so hreflang stays accurate even on locale-only paths like "/en".
  const altPath = (l: string) => pathname.replace(langSegmentRegex(), `/${l}`);
  const orgLd = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "@id": "https://majourneys.com/#organization",
    name: "MaJourneys",
    legalName: "MAJOURNEYS SARL",
    url: "https://majourneys.com",
    logo: {
      "@type": "ImageObject",
      url: "https://majourneys.com/images/majourneys-logo-light.svg",
      width: 512,
      height: 512,
    },
    image: {
      "@type": "ImageObject",
      url: "https://majourneys.com/images/majourneys_who_we_are.webp",
      width: 1200,
      height: 630,
    },
    description,
    // Top-level PostalAddress + telephone + email for knowledge-panel
    // eligibility. Mirrors contactPoint so consumers that only look at
    // top-level Organization fields still get the canonical NAP.
    address: {
      "@type": "PostalAddress",
      addressLocality: "Aïn Sebaâ, Casablanca",
      addressRegion: "Casablanca-Settat",
      addressCountry: "MA",
    },
    telephone: WHATSAPP_NUMBER_E164,
    email: CONTACT_EMAIL,
    areaServed: { "@type": "Country", name: countryName },
    availableLanguage: ["en", "fr", "ar"],
    currenciesAccepted: ["MAD", "USD", "EUR"],
    paymentAccepted: t("home.seo_payment_accepted"),
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: WHATSAPP_NUMBER_E164,
      email: CONTACT_EMAIL,
      availableLanguage: ["en", "fr", "ar"],
      areaServed: "MA",
    },
    sameAs: [
      "https://www.instagram.com/majourneys.ma",
      "https://www.facebook.com/majourneys",
      "https://www.tiktok.com/@majourneys",
      "https://www.pinterest.com/majourneys",
      "https://twitter.com/majourneys",
      "https://www.youtube.com/@majourneys",
      "https://www.linkedin.com/company/majourneys",
    ],
  };
  const siteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://majourneys.com/#website",
    name: "MaJourneys",
    url: "https://majourneys.com",
    inLanguage: ["en-US", "fr-MA", "ar-MA"],
    publisher: { "@id": "https://majourneys.com/#organization" },
    potentialAction: {
      "@type": "SearchAction",
      target: "https://majourneys.com/en/explore?q={search_term}",
      "query-input": "required name=search_term",
    },
  };
  const destLd = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    name: countryName,
    description,
    touristType: ["Adventure", "Culture", "Family", "Solo", "Luxury"],
    includesAttraction: REGIONS.map((r) => ({
      "@type": "TouristAttraction",
      name: t(`home.regions.${r.key}.name`, { defaultValue: r.key }),
      description: t(`home.regions.${r.key}.blurb`, { defaultValue: "" }),
    })),
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="MaJourneys" />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={ogLocale} />
      {SUPPORTED_LANGS
        .filter((l) => l !== lang)
        .map((l) => (
          <meta
            key={`alt-${l}`}
            property="og:locale:alternate"
            content={OG_LOCALE[l]}
          />
        ))}
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <meta name="geo.region" content="MA" />
      <meta name="geo.country" content={countryName} />
      <meta name="geo.placename" content={countryName} />
      <meta name="geo.position" content="31.7917;-7.0926" />
      <meta name="ICBM" content="31.7917, -7.0926" />
      <link rel="canonical" href={url} />
      {SUPPORTED_LANGS.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${siteOrigin}${altPath(l)}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${siteOrigin}${altPath("en")}`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(siteLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(destLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
    </>
  );
}

function BackgroundOrbs() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute -left-32 top-0 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(229,178,101,0.32)_0%,rgba(229,178,101,0)_70%)] blur-3xl" />
      <div className="absolute right-[-10%] top-[18%] h-[600px] w-[600px] rounded-full bg-[radial-gradient(circle,rgba(130,120,255,0.26)_0%,rgba(130,120,255,0)_70%)] blur-3xl" />
      <div className="absolute left-[30%] top-[60%] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(255,145,170,0.20)_0%,rgba(255,145,170,0)_70%)] blur-3xl" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#ffffff_0%,rgba(247,246,252,0.45)_28%,rgba(250,248,250,0.35)_55%,rgba(253,250,246,0.8)_100%)]" />
      {/* The warm pool that used to live inside the hero. It sits above the
          base wash (which is near-opaque white at the top and would swallow
          it) and crosses the hero's bottom edge, fading out over the sections
          below instead of being clipped into a hard line. */}
      <div className="absolute left-1/2 top-[34vh] h-[1200px] w-[1800px] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(closest-side,rgba(229,178,101,0.34)_0%,rgba(229,178,101,0.13)_45%,rgba(229,178,101,0)_76%)] blur-3xl" />
    </div>
  );
}

function HeroSkeleton() {
  return <div className="h-[520px] w-full animate-pulse bg-slate-100/50" />;
}

function RailSkeleton() {
  return (
    <section className="relative mx-auto w-full max-w-[1320px] px-4 py-3 sm:px-6 md:py-4 lg:px-8 lg:py-5" aria-hidden>
      <div className="mb-3 h-[68px] md:mb-3.5" />
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:overflow-x-visible sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex min-w-max gap-4 sm:grid sm:min-w-0 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="w-[204px] sm:w-auto">
              <div className="aspect-square w-full animate-pulse rounded-[16px] bg-slate-100/60" />
              <div className="mt-2 h-4 w-3/4 animate-pulse rounded bg-slate-100/60" />
              <div className="mt-1.5 h-3 w-1/2 animate-pulse rounded bg-slate-100/60" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Section({
  children,
  className = "",
  as: As = "section",
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly as?: "section" | "div";
}) {
  return (
    <As className={`relative mx-auto w-full max-w-[1320px] px-4 py-3 sm:px-6 md:py-4 lg:px-8 lg:py-5 ${className}`}>
      {children}
    </As>
  );
}

function SectionHeader({
  eyebrow,
  title,
  description,
  action,
  align = "start",
}: {
  readonly eyebrow?: string;
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly align?: "start" | "center";
}) {
  const alignCls = align === "center" ? "items-center text-center" : "items-start text-start";
  const mxCls = align === "center" ? "mx-auto" : "";
  return (
    <div className={`mb-3 flex flex-row items-end justify-between gap-3 md:mb-3.5 ${align === "center" ? "md:flex-col md:items-center" : ""}`}>
      <div className={`flex min-w-0 flex-1 flex-col gap-2 ${alignCls} ${mxCls} ${align === "center" ? "max-w-[720px]" : "max-w-[640px]"}`}>
        {eyebrow && (
          <span className="font-sans text-[11px] font-bold uppercase tracking-[0.14em] text-[#5a5c75]">
            {eyebrow}
          </span>
        )}
        <h2 className="font-heading text-balance text-[26px] font-bold leading-[1.15] tracking-[-0.01em] text-[#110E39] sm:text-[30px] md:text-[36px]">
          {title}
        </h2>
        {description && (
          <p className="text-[14.5px] leading-relaxed text-[#525466] md:text-[16px]">{description}</p>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

/**
 * "See all" in pewter, and a shadow so it lifts off the page rather than
 * sitting flush in it (owner, 2026-09-08). It shares the carousel arrows'
 * geometry on purpose — the two sit on the same section headers and were a
 * bordered pill next to a framed disc.
 *
 * 2px band: at 34px tall anything wider closes over the label.
 *
 * `sheen` rather than the house `pewter` (owner, 2026-09-13). Pewter is a
 * symmetric five-stop turn across 155°, which needs a wide edge to travel
 * along; compressed into a 34px pill the middle stop wins and the band reads
 * as a flat grey border. Sheen has a single transition across the whole shape,
 * so it still reads as light at this size.
 */
const SEE_ALL_GAP = 2;
const SEE_ALL_BASE = pewterFrame(999, { gap: SEE_ALL_GAP, style: "sheen" });
const SEE_ALL_FRAME = {
  ...SEE_ALL_BASE,
  boxShadow: `${SEE_ALL_BASE.boxShadow}, 0 4px 12px rgba(17,14,57,0.07)`,
};
const SEE_ALL_INNER = pewterInner(solid("rgba(255,255,255,1)"), 999, { gap: SEE_ALL_GAP });

function SeeAllLink({ href, label }: { readonly href: string; readonly label?: string }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  return (
    <a
      href={href}
      className="mj-slider-arrow inline-block transition-transform duration-200 hover:-translate-y-[1px]"
      style={SEE_ALL_FRAME}
    >
      <span
        className="inline-flex h-[34px] items-center gap-1.5 px-4 font-sans text-[13px] font-semibold text-[#110E39]"
        style={SEE_ALL_INNER}
      >
        {label ?? t("common.see_all")}
        <Icon name={isRtl ? "ri-arrow-left-line" : "ri-arrow-right-line"} className={`text-[14px]`} aria-hidden="true" />
      </span>
    </a>
  );
}

function WomenHomeEntryBanner() {
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  return <WomenHomeBanner lang={lang ?? "en"} />;
}

function CategoryStrip() {
  const { t } = useTranslation();
  const { data: response } = useCategories();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  // The app's order: activities, restaurants, spas, stays, then the rest.
  const categories = rankCategories(
    (response.data as Array<{ id: string; slug: string; name: string; icon?: string }> | undefined) ?? [],
  );
  if (categories.length === 0) return <div className="h-[180px] w-full" aria-hidden />;
  return (
    <Section>
      <SectionHeader
        eyebrow={t("home.browse_eyebrow", { defaultValue: "Browse" })}
        title={t("home.browse_title", { defaultValue: "Pick your vibe" })}
      />
      {/*
        `overflow-x-auto` computes overflow-y to `auto` as well, so this rail
        clips vertically too. The chips lift on hover/tap (-translate-y + a
        taller shadow), so without headroom the lifted top edge and its shadow
        get sliced flat against the container. `pt-2` buys that headroom and
        `-mt-2` cancels it so the row doesn't shift down.
      */}
      <nav aria-label={t("home.categories_nav", { defaultValue: "Categories" })} className="-mx-4 -mb-6 -mt-3 overflow-x-auto px-4 pb-8 pt-3 [scrollbar-width:none] sm:mx-0 sm:overflow-x-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
        <ul className="flex min-w-max gap-2.5 sm:min-w-0 sm:flex-wrap">
          {categories.map((c) => (
            <li key={c.id}>
              <Link
                to="/$lang/category/$category"
                params={{ lang: locale, category: c.slug }}
                className={CATEGORY_PILL_CLASS}
                style={categoryPillFrameStyle(false, "lg")}
                // The category page plays this category's tap effect on its
                // tab when it lands (shared/ui/category-tap-fx.ts).
                onClick={() => queueCategoryFx(c.slug)}
              >
                <CategoryPillBody name={c.name} thumbnail={c.icon} size="lg" />
              </Link>
            </li>
          ))}
          {/* Women-only trips, last in the row; hidden while none is coming up. */}
          <WomenCategoryChip lang={locale} />
        </ul>
      </nav>
    </Section>
  );
}


/**
 * `activitiesCount` counts PUBLISHED BOOKABLE activities only (listCities in
 * apps/api/src/features/_shared/explore-data.ts) — explore-mode editorial rows
 * are deliberately excluded from it.
 *
 * So a city we cover editorially but cannot yet sell reports 0 and, without
 * this filter, renders a card reading "0 activities" with no hero image. The
 * grid sorts by count and slices, it never filtered, so every such city landed
 * on the homepage the moment its `cities` row existed.
 */
function hasBookableInventory(city: { activitiesCount?: number }): boolean {
  return (city.activitiesCount ?? 0) > 0;
}

function CitiesCarousel() {
  const { t } = useTranslation();
  const { data: response } = useCities();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const cities = (response.data as City[] | undefined) ?? [];
  if (cities.length === 0) return <div className="h-[420px] w-full" aria-hidden />;
  const sorted = [...cities]
    .filter(hasBookableInventory)
    .sort((a, b) => (b.activitiesCount ?? 0) - (a.activitiesCount ?? 0))
    .slice(0, 20);

  return (
    <Section>
      <SectionHeader
        eyebrow={t("home.destinations_eyebrow", { defaultValue: "Destinations" })}
        title={t("home.destinations_title", { defaultValue: "Explore by city" })}
        action={<SeeAllLink href={`/${locale}/place`} label={t("common.see_all", { defaultValue: "See all" })} />}
      />
      <CityTrack cities={sorted} locale={locale} />
    </Section>
  );
}

function CityTrack({ cities, locale }: { readonly cities: City[]; readonly locale: string }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [emblaRef, emblaApi] = useEmblaCarousel({
    direction: isRtl ? "rtl" : "ltr",
    align: "start",
    containScroll: "trimSnaps",
    slidesToScroll: 1,
    loop: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [selected, setSelected] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setSnapCount(emblaApi.scrollSnapList().length);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => {
      onSelect();
      setSnapCount(emblaApi.scrollSnapList().length);
    });
  }, [emblaApi, onSelect]);

  const prev = () => emblaApi?.scrollPrev();
  const next = () => emblaApi?.scrollNext();

  return (
    <div className="relative">
      {/* pt-2/-mt-2: the cards lift 4px on hover and this viewport hard-clips. */}
      <div className="overflow-hidden -mx-4 -mt-2 px-4 pt-2 sm:mx-0 sm:px-0" ref={emblaRef}>
        <div className="flex gap-3 sm:gap-4">
          {cities.map((c, idx) => (
            <div
              key={c.id}
              className="min-w-0 flex-[0_0_41%] sm:flex-[0_0_33%] md:flex-[0_0_25%] lg:flex-[0_0_20%] xl:flex-[0_0_16.6%]"
            >
              {/* The first cities are what a phone sees first: fetch them ahead of
                  the rails further down, which all wait their turn (lazy). */}
              <CityCard city={c} locale={locale} eager={idx < 3} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        {snapCount > 1 ? (
          <>
          {/* Phones: one track with a sliding thumb. A dot per city wrapped
              onto two lines at 390 (owner check, 2026-10-12); the arrows
              beside it still move the row. */}
          <div className="relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-[rgba(4,0,51,0.12)] sm:hidden" aria-hidden="true">
            <span
              className="absolute inset-y-0 start-0 rounded-full bg-[#110E39] transition-transform duration-300 ease-out"
              style={{
                width: `${100 / snapCount}%`,
                transform: `translateX(${(isRtl ? -1 : 1) * selected * 100}%)`,
              }}
            />
          </div>
          <div className="hidden min-w-0 flex-1 flex-wrap items-center sm:flex">
            {Array.from({ length: snapCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => emblaApi?.scrollTo(i)}
                aria-label={t("home.go_to_slide", { defaultValue: "Go to slide {{number}}", number: i + 1 })}
                className="flex h-6 min-w-6 items-center justify-center px-1"
              >
                <span
                  className={`block h-1.5 rounded-full transition-all duration-300 ${
                    i === selected ? "w-7 bg-[#110E39]" : "w-1.5 bg-[rgba(4,0,51,0.18)] hover:bg-[rgba(4,0,51,0.32)]"
                  }`}
                />
              </button>
            ))}
          </div>
          </>
        ) : (
          <span />
        )}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={prev}
            disabled={!canPrev}
            aria-label={t("home.previous_cities", { defaultValue: "Previous cities" })}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(4,0,51,0.08)] bg-white text-[#110E39] shadow-[0_4px_14px_rgba(4,0,51,0.06)] transition hover:-translate-y-[1px] hover:shadow-[0_10px_24px_rgba(4,0,51,0.10)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:translate-y-0 disabled:hover:shadow-[0_4px_14px_rgba(4,0,51,0.06)]"
          >
            <Icon name={isRtl ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} className={`text-[22px]`} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!canNext}
            aria-label={t("home.next_cities", { defaultValue: "Next cities" })}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(4,0,51,0.08)] bg-white text-[#110E39] shadow-[0_4px_14px_rgba(4,0,51,0.06)] transition hover:-translate-y-[1px] hover:shadow-[0_10px_24px_rgba(4,0,51,0.10)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:translate-y-0 disabled:hover:shadow-[0_4px_14px_rgba(4,0,51,0.06)]"
          >
            <Icon name={isRtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"} className={`text-[22px]`} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface Activity {
  isNew?: boolean;
  id: string;
  slug: string;
  title: string;
  images: string[];
  price: number;
  typePrice?: number;
  rating?: number | string;
  mode?: "bookable" | "explore";
  segment?: string | null;
  category?: { name: string; slug?: string } | null;
  city?: { name: string; slug?: string };
}

function ActivityRail({
  eyebrow,
  title,
  subtitle,
  items,
  href,
  locale,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly subtitle: string;
  readonly items: Activity[];
  readonly href: string;
  readonly locale: string;
}) {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;
  return (
    <Section>
      <SectionHeader
        eyebrow={eyebrow}
        title={title}
        action={<SeeAllLink href={href} label={t("common.see_all", { defaultValue: "See all" })} />}
      />
      <div className="-mx-4 -mb-6 -mt-8 overflow-x-auto px-4 pb-8 pt-8 sm:mx-0 sm:overflow-x-visible sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex min-w-max gap-4 sm:grid sm:min-w-0 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.slice(0, 10).map((a, idx) => (
            <div key={a.id} className="flex w-[240px] justify-center sm:w-auto">
              <ActivityCard2
                slug={a.slug}
                locale={locale}
                image={a.images[0]}
                images={a.images}
                title={a.title}
                city={a.city?.name ?? ""}
                citySlug={a.city?.slug}
                category={a.category?.name}
                categorySlug={a.category?.slug}
                segment={a.segment ?? null}
                price={a.price}
                isNew={a.isNew === true}
                priceType={a.typePrice}
              />
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function TrendingRail() {
  const { t } = useTranslation();
  const { data } = useTrendingActivities();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const items = (data.data as Activity[]) ?? [];
  if (items.length === 0) return null;
  return (
    <ActivityRail
      eyebrow={t("home.most_booked_eyebrow", { defaultValue: "Most booked" })}
      title={t("home.most_booked_title", { defaultValue: "Trending this week" })}
      subtitle={t("home.most_booked_subtitle", { defaultValue: "What travelers are booking right now." })}
      items={items}
      href={`/${locale}/explore?sort=trending`}
      locale={locale}
    />
  );
}

function OffersRail() {
  const { t } = useTranslation();
  const { data } = useOffers();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const items = (data.data as Activity[]) ?? [];
  if (items.length === 0) return null;
  return (
    <ActivityRail
      eyebrow={t("home.limited_time_eyebrow", { defaultValue: "Limited time" })}
      title={t("home.limited_time_title", { defaultValue: "Last-minute deals" })}
      subtitle={t("home.limited_time_subtitle", { defaultValue: "Operator-curated discounts, updated often." })}
      items={items}
      href={`/${locale}/explore`}
      locale={locale}
    />
  );
}

function NewRail() {
  const { t } = useTranslation();
  const { data } = useNewActivities();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const items = (data.data as Activity[]) ?? [];
  if (items.length === 0) return null;
  return (
    <ActivityRail
      eyebrow={t("home.new_eyebrow", { defaultValue: "Just added" })}
      title={t("home.new_title", { defaultValue: "New on MaJourneys" })}
      subtitle={t("home.new_subtitle", { defaultValue: "Fresh experiences from vetted local operators." })}
      items={items}
      href={`/${locale}/explore?sort=newest`}
      locale={locale}
    />
  );
}

function isNotFound(error: unknown): boolean {
  return (error as { status?: unknown } | null | undefined)?.status === 404;
}

function RecentViewedRail() {
  const { t } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const items = useRecentViewed();

  const visible = useMemo(() => items.slice(0, 10), [items]);

  // Refetch each cached slug in the current locale so titles/city/category
  // render in fr+ar (cache stores whatever locale was active at view time).
  const detailQueries = useQueries({
    queries: visible.map((item) => ({
      queryKey: activityKeys.detail(item.slug, locale),
      queryFn: () => activitiesApi.getBySlug(item.slug, locale),
      staleTime: 5 * 60 * 1000,
      // A 404 will not heal on retry: the listing was removed.
      retry: (count: number, error: unknown) => !isNotFound(error) && count < 2,
    })),
  });

  // The slugs live in this browser's storage, so a listing removed from the
  // catalogue keeps being requested (and 404ing) on every home visit until
  // it is pruned. Drop it the first time the API says it is gone.
  // Transfer routes are sold on the /transport hub only and have no page of
  // their own, but browsers that opened one before that still hold its slug.
  // Treat them like removed listings: hide and forget.
  const isTransfer = (idx: number) => {
    const fresh = (detailQueries[idx]?.data as { data?: { category?: { slug?: string } | null } } | undefined)?.data;
    return isTransferBooking({ categorySlug: fresh?.category?.slug ?? null });
  };
  const goneSlugs = visible
    .filter((_, idx) => isNotFound(detailQueries[idx]?.error) || isTransfer(idx))
    .map((item) => item.slug);
  const goneKey = goneSlugs.join("|");
  useEffect(() => {
    if (!goneKey) return;
    for (const slug of goneKey.split("|")) removeRecentViewed(slug);
  }, [goneKey]);

  if (items.length === 0) return null;

  return (
    <Section>
      <SectionHeader
        eyebrow={t("home.recent_viewed_eyebrow", { defaultValue: "Picked up where you left off" })}
        title={t("home.recent_viewed_title", { defaultValue: "Recently viewed" })}
      />
      <div className="-mx-4 -mb-6 -mt-8 overflow-x-auto px-4 pb-8 pt-8 sm:mx-0 sm:overflow-x-visible sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex min-w-max gap-4 sm:grid sm:min-w-0 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {visible.map((item, idx) => {
            const fresh = (detailQueries[idx]?.data as { data?: unknown } | undefined)?.data as
              | {
                  title?: string;
                  city?: { name?: string; slug?: string };
                  category?: { name?: string; slug?: string } | null;
                  segment?: string | null;
                  mode?: "bookable" | "explore";
                  images?: string[];
                  price?: number;
                  typePrice?: string | number;
                }
              | undefined;
            // Skip explore-mode items — this rail is for bookable cards only.
            if (fresh?.mode === "explore") return null;
            if (isNotFound(detailQueries[idx]?.error)) return null;
            if (isTransfer(idx)) return null;
            const shown = recentViewedDisplay(item, locale, fresh);
            if (!shown) return null;
            return (
              <div key={item.slug} className="flex w-[240px] justify-center sm:w-auto">
                <ActivityCard2
                  slug={item.slug}
                  locale={locale}
                  image={fresh?.images?.[0] ?? item.image}
                  images={fresh?.images}
                  title={shown.title}
                  city={shown.city}
                  citySlug={fresh?.city?.slug}
                  category={shown.category}
                  categorySlug={fresh?.category?.slug}
                  segment={fresh?.segment ?? null}
                  price={fresh?.price ?? item.price}
                  currency={item.currency}
                  priceType={fresh?.typePrice ?? item.priceType}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function CategoryActivityRail({
  slug,
  eyebrow,
  title,
  subtitle,
}: {
  readonly slug: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly subtitle: string;
}) {
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const filters = { category: slug, page_size: "10", lang: locale } as const;
  const { data } = useSuspenseQuery({
    queryKey: activityKeys.list(filters),
    queryFn: () => activitiesApi.list(filters),
    staleTime: 60_000,
  });
  const items = ((data as { data?: Activity[] }).data as Activity[] | undefined) ?? [];
  if (items.length === 0) return null;
  return (
    <ActivityRail
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      items={items}
      href={`/${locale}/category/${slug}`}
      locale={locale}
    />
  );
}

function CityActivityRail({
  slug,
  eyebrow,
  title,
  subtitle,
}: {
  readonly slug: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly subtitle: string;
}) {
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  // `sort=mixed` interleaves the city's categories. Without it these ten
  // slots come back in `id ASC` order, and a city like Marrakech — ~43
  // partner villas against a handful of everything else — showed a rail of
  // near-identical villa cards with no excursion, workshop or city tour in
  // sight. The full list is still one page-load away behind "See all".
  const filters = { city: slug, page_size: "10", sort: "mixed", lang: locale } as const;
  const { data } = useSuspenseQuery({
    queryKey: activityKeys.list(filters),
    queryFn: () => activitiesApi.list(filters),
    staleTime: 60_000,
  });
  const { t } = useTranslation();
  const items = ((data as { data?: Activity[] }).data as Activity[] | undefined) ?? [];
  // "24 things to book" under the city's name, as in the app: the search's
  // total, so it matches what "See all" opens. "City" only if it is missing.
  const total = (data as { total?: number }).total ?? 0;
  if (items.length === 0) return null;
  return (
    <ActivityRail
      eyebrow={total > 0 ? t("home.city_book_count", { count: total }) : eyebrow}
      title={title}
      subtitle={subtitle}
      items={items}
      href={`/${locale}/place/${slug}`}
      locale={locale}
    />
  );
}

function RegionsSection() {
  const { t } = useTranslation();
  const { data: response } = useCities();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  // Every city we sell in, read from the API, so a new city row shows up here
  // on its own. The old hard-coded REGIONS list left out half the cities.
  const cities = [...((response.data as City[] | undefined) ?? [])]
    .filter(hasBookableInventory)
    .sort((a, b) => (b.activitiesCount ?? 0) - (a.activitiesCount ?? 0));
  const fallbackBySlug = new Map<string, string>(
    REGIONS.flatMap((r) => (r.citySlug ? [[r.citySlug, r.fallbackImage] as const] : [])),
  );
  if (cities.length === 0) return null;
  return (
    <Section>
      <SectionHeader
        eyebrow={t("home.regions_eyebrow", { defaultValue: "Regions" })}
        title={t("home.regions_title", { defaultValue: "Morocco, region by region" })}
        action={<SeeAllLink href={`/${locale}/explore`} label={t("common.see_all", { defaultValue: "See all" })} />}
      />
      {/* Desktop only (hidden below md). 7 across, so 13 cities split 7 + 6. */}
      <div className="grid grid-cols-7 gap-x-4 gap-y-6">
        {cities.map((city) => {
          const image = city.image || fallbackBySlug.get(city.slug);
          const name = city.name;
          const card = (
            <>
              <div
                className="relative mx-auto border border-[rgba(229,178,101,0.5)] p-[4px] transition-colors duration-300 group-hover:border-[#E5B265]"
                style={{ borderRadius: "999px 999px 14px 14px" }}
              >
                <div className="overflow-hidden" style={{ borderRadius: "999px 999px 10px 10px" }}>
                  <div className="aspect-[3/4]">
                    <ImageWithFallback
                      src={image}
                      alt=""
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.08]"
                    />
                  </div>
                </div>
              </div>
              <h3 className="mt-2.5 break-words [text-wrap:balance] font-heading text-[13.5px] font-bold text-[#110E39]">{name}</h3>
            </>
          );
          const cardCls = "group block text-center transition-transform duration-300 hover:-translate-y-1.5";
          return (
            <Link key={city.id} to="/$lang/place/$city" params={{ lang: locale, city: city.slug }} className={cardCls}>
              {card}
            </Link>
          );
        })}
      </div>
    </Section>
  );
}

function FaqSection() {
  const { t } = useTranslation();
  const faqs = t("home.faqs", { returnObjects: true }) as ReadonlyArray<{ q: string; a: string }>;
  return (
    <Section className="!max-w-[880px]">
      <SectionHeader
        eyebrow={t("home.faq_eyebrow", { defaultValue: "FAQ" })}
        title={t("home.faq_title", { defaultValue: "Questions, answered" })}
        align="center"
      />
      {/* One panel, not four floating cards. Still <details> rather than React
          state: a closed answer stays in the server-rendered HTML, which is the
          point of having an FAQ on a page Google reads. */}
      <div className="overflow-hidden" style={panelStyle(22)}>
        {faqs.map((f, i) => (
          <details
            key={f.q}
            className="group px-5 py-1 md:px-6"
            style={i === 0 ? undefined : { boxShadow: "inset 0 1px 0 rgba(17,14,57,0.09)" }}
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-[14.5px] font-bold text-[#110E39] md:text-[16px]">
              {f.q}
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(17,14,57,0.055)] text-[#5a5d77] transition group-open:rotate-45 group-open:bg-[#110E39] group-open:text-white">
                <Icon name="ri-add-line" className="text-[16px]" aria-hidden="true" />
              </span>
            </summary>
            <p className="pb-4 pe-10 text-[13.5px] leading-relaxed text-[#5a5d77] md:text-[14.5px]">
              {f.a}
            </p>
          </details>
        ))}
      </div>
    </Section>
  );
}

function SeoFooterSection() {
  const { t } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const { data: citiesResp } = useCities();
  const { data: catsResp } = useCategories();
  const { data: trendingResp } = useTrendingActivities();

  const cities = (citiesResp.data as Array<{ id: string; slug: string; name: string; activitiesCount?: number }> | undefined) ?? [];
  const categories = (catsResp.data as Array<{ id: string; slug: string; name: string }> | undefined) ?? [];
  const trending = ((trendingResp.data as Activity[] | undefined) ?? []).slice(0, 18);

  const sortedCities = [...cities]
    .filter(hasBookableInventory)
    .sort((a, b) => (b.activitiesCount ?? 0) - (a.activitiesCount ?? 0))
    .slice(0, 16);

  const tabs = [
    {
      id: "destinations" as const,
      label: t("home.top_destinations", { defaultValue: "Top destinations" }),
      items: sortedCities.map((c) => ({ key: c.id, label: c.name, href: `/${locale}/place/${c.slug}` })),
    },
    {
      id: "categories" as const,
      label: t("home.popular_categories", { defaultValue: "Popular categories" }),
      items: categories.map((c) => ({ key: c.id, label: c.name, href: `/${locale}/category/${c.slug}` })),
    },
    {
      id: "experiences" as const,
      label: t("home.top_experiences", { defaultValue: "Top experiences" }),
      items: trending.map((a) => ({
        key: a.id,
        label: a.title,
        href: activityHrefString({
          lang: locale,
          citySlug: a.city?.slug ?? null,
          categorySlug: a.category?.slug ?? null,
          segment: a.segment ?? null,
          slug: a.slug,
        }),
      })),
    },
  ];

  const [active, setActive] = useState<typeof tabs[number]["id"]>("destinations");
  const tab = tabs.find((t) => t.id === active)!;
  if (tab.items.length === 0) return null;

  return (
    <Section>
      <SectionHeader
        eyebrow={t("home.sitemap_eyebrow", { defaultValue: "Sitemap" })}
        title={t("home.sitemap_title", { defaultValue: "Discover Morocco" })}
      />
      {/* Chips rather than a four-column numbered grid. The numbering counted
          nothing, and on a phone the grid became thirteen stacked rows; chips
          wrap. Every href is unchanged — this block exists for the internal
          link graph, so the links are the part that must not move. */}
      <div className="p-5 md:p-6" style={panelStyle(22)}>
        <div className="inline-flex flex-wrap gap-1 rounded-full bg-[rgba(17,14,57,0.055)] p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t.id)}
              className={`inline-flex h-9 items-center rounded-full px-3.5 font-sans text-[12.5px] font-bold transition ${
                active === t.id ? "bg-[#110E39] navy-depth text-white" : "text-[#5a5d77] hover:text-[#110E39]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <ul className="mt-4 flex flex-wrap gap-2">
          {tab.items.map((item) => (
            <li key={item.key}>
              <a
                href={item.href}
                className="inline-flex max-w-full items-center rounded-full bg-white/90 px-3.5 py-2 font-sans text-[13px] font-semibold text-[#110E39] shadow-[0_0_0_1px_rgba(17,14,57,0.09)] transition hover:bg-white hover:shadow-[0_0_0_1px_rgba(17,14,57,0.22)]"
              >
                <span className="break-words">{item.label}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}

